import { Injectable } from '@angular/core';
import { Network } from '@capacitor/network';
import { environment } from '../../environments/environment';

/** What a failed request actually means, once device- and service-level connectivity are both checked. */
export type ConnectivityFailure = 'offline' | 'service-down' | 'transient';

const SERVICE_PROBE_TIMEOUT_MS = 6_000;

/**
 * Distinguishes "your device has no network" from "our service is down" from "that one request
 * just hiccuped" — a single connected:true/false flag can't tell these apart, especially on
 * web/PWA (see isDeviceOnline's doc). Both checks work identically on native and web/PWA at the
 * call-site level (Capacitor's Network plugin and a plain fetch() both already resolve to the
 * platform-appropriate mechanism under the hood), but the two platforms warrant different trust
 * in what each signal actually means — see the per-method docs below.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {

  /**
   * Device-level: is there a network interface up at all?
   * - Native: @capacitor/network calls the OS's real reachability API (iOS/Android) — a
   *   `connected: false` here is trustworthy.
   * - Web/PWA: this is a thin wrapper over `navigator.onLine`, which only reflects "is some
   *   network interface up," not "is the internet actually reachable" — it reports `true` on a
   *   Wi-Fi network with no upstream internet, behind a captive portal, etc. A `false` here is
   *   still trustworthy (browsers rarely false-negative); a `true` should NOT be treated as
   *   proof of real connectivity — that's what isServiceReachable() is for.
   */
  async isDeviceOnline(): Promise<boolean> {
    const status = await Network.getStatus();
    return status.connected;
  }

  /**
   * Service-level: can we actually reach our own API right now? A real, lightweight probe (the
   * same public config/version-check endpoint already called at app boot), distinct from
   * whatever request just failed — so a CORS block, a timeout, or an aborted request on ONE
   * endpoint doesn't get mistaken for "the whole service is down."
   *
   * Deliberately uses a raw fetch(), NOT Angular's HttpClient — HttpClient goes through
   * HttpTimeoutInterceptor, which is what calls this probe in the first place (on a status-0
   * failure); routing the probe back through that same interceptor would recurse into it again
   * on every failed probe, unboundedly, for exactly as long as a real outage lasts. A raw fetch
   * still gets the platform-appropriate transport for free: on native, Capacitor's CapacitorHttp
   * plugin (enabled in capacitor.config.json) patches the global fetch to route over its native
   * HTTP bridge, bypassing WebView CORS entirely, so a failure here is unambiguous; on web this
   * is a real browser fetch and can — rarely — also fail for reasons unrelated to the server
   * itself (e.g. a content-blocking browser extension), which is an inherent web platform
   * limitation this probe can't fully rule out.
   *
   * Success means "the server responded at all" — any HTTP status counts, including 4xx/5xx,
   * since only a network-level failure (DNS, connection refused, CORS block, our own timeout)
   * makes fetch() itself throw rather than resolve.
   */
  async isServiceReachable(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SERVICE_PROBE_TIMEOUT_MS);
    try {
      await fetch(`${environment.apiServer}/v1/config/version-check`, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /** Classifies a request failure by checking device connectivity first, then (only if the
   *  device claims to be online) verifying the service itself — see isDeviceOnline()'s doc for
   *  why "online" alone isn't trusted at face value, especially on web/PWA. */
  async classifyFailure(): Promise<ConnectivityFailure> {
    const online = await this.isDeviceOnline();
    if (!online) {
      return 'offline';
    }
    const reachable = await this.isServiceReachable();
    return reachable ? 'transient' : 'service-down';
  }

  /** Real connection-type detail ("wifi"/"cellular") only when it's actually meaningful — null
   *  for "none" (nothing to name) and for "unknown", which @capacitor/network's web
   *  implementation reports on any browser without the non-standard, Chromium-only Network
   *  Information API (Safari and Firefox included) — callers should omit the detail entirely
   *  rather than surface a literal "unknown" (or "none") network name to the user. */
  async describeConnectionType(): Promise<string | null> {
    const status = await Network.getStatus();
    return status.connectionType === 'wifi' || status.connectionType === 'cellular'
      ? status.connectionType
      : null;
  }
}
